import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail, ROLE_HOME } from "@/lib/api";
import { Logo } from "@/components/Logo";
import PhoneInput from "@/components/PhoneInput";

export default function Register() {
  const { register } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "parent", phone: "" });
  const [phoneValid, setPhoneValid] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.phone || !phoneValid) {
      setErr("Please enter a valid phone number with country code.");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const u = await register(form);
      navigate(ROLE_HOME[u.role] || "/app/parent", { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-3xl border border-border p-8 shadow-soft">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="flex items-center gap-2.5" data-testid="register-brand">
            <Logo size={42} tagline={true} />
          </Link>
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">{t("register.title")}</h1>
        <p className="text-ink-soft text-sm mt-1">{t("register.subtitle")}</p>
        <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="register-form">
          <input data-testid="register-name" required placeholder={t("register.fullNamePlaceholder")} value={form.name} onChange={e=>setForm({...form, name: e.target.value})} className="w-full h-12 px-4 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-terracotta" />
          <input data-testid="register-email" required type="email" placeholder={t("auth.emailLabel")} value={form.email} onChange={e=>setForm({...form, email: e.target.value})} className="w-full h-12 px-4 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-terracotta" />
          <PhoneInput
            value={form.phone}
            onChange={(v) => setForm({ ...form, phone: v })}
            onValidityChange={setPhoneValid}
            required
            placeholder="Phone number"
            testId="register-phone"
          />
          <input data-testid="register-password" required type="password" placeholder={t("auth.passwordLabel") + " · " + t("register.passwordHint")} value={form.password} onChange={e=>setForm({...form, password: e.target.value})} className="w-full h-12 px-4 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-terracotta" />
          {err && <div className="text-sm text-red-600" data-testid="register-error">{err}</div>}
          <button disabled={loading || !phoneValid} className="w-full h-12 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta-hover disabled:opacity-60" data-testid="register-submit">
            {loading ? t("register.creating") : t("register.createBtn")}
          </button>
        </form>
        <p className="text-sm text-ink-soft mt-6 text-center">
          {t("auth.alreadyAccount")} <Link to="/login" className="text-terracotta font-semibold">{t("auth.signIn")}</Link>
        </p>
      </div>
    </div>
  );
}
